import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <Header />

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8 font-['Outfit']">Privacy Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                QuickFinance.io ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website quickfinance.io and use our services.
              </p>
              <p className="text-slate-600 leading-relaxed">
                By using our website and services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">2. Information We Collect</h2>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Personal Information</h3>
              <p className="text-slate-600 leading-relaxed mb-4">When you apply for funding or contact us, we may collect:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Full name and contact details (email, phone number, address)</li>
                <li>Business name and registration details</li>
                <li>Financial information (turnover, revenue, funding requirements)</li>
                <li>Credit history information (CCJs, defaults)</li>
                <li>Industry and business type</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Automatically Collected Information</h3>
              <p className="text-slate-600 leading-relaxed mb-4">We automatically collect certain information when you visit our website:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>IP address and browser type</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Device information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">3. How We Use Your Information</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Process your funding application and assess eligibility</li>
                <li>Connect you with appropriate funding partners</li>
                <li>Communicate with you about your application and our services</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Improve our website and services</li>
                <li>Send marketing communications (only with your consent)</li>
                <li>Prevent fraud and maintain security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">4. Information Sharing</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Funding Partners:</strong> To assess your application and provide funding offers</li>
                <li><strong>Credit Reference Agencies:</strong> To verify information and assess creditworthiness</li>
                <li><strong>Service Providers:</strong> Who assist us in operating our business</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                We do not sell your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">5. Your Rights Under GDPR</h2>
              <p className="text-slate-600 leading-relaxed mb-4">Under the UK General Data Protection Regulation (UK GDPR), you have the right to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Restriction:</strong> Request limitation of processing</li>
                <li><strong>Portability:</strong> Request transfer of your data</li>
                <li><strong>Objection:</strong> Object to processing for marketing purposes</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                To exercise any of these rights, please contact us at privacy@quickfinance.io
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">6. Data Security</h2>
              <p className="text-slate-600 leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">7. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed">
                We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, including legal, accounting, or reporting requirements. Typically, we retain application data for 6 years after our relationship ends.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">8. Cookies</h2>
              <p className="text-slate-600 leading-relaxed">
                We use cookies and similar tracking technologies on our website. For more information, please see our <a href="/cookies" className="text-blue-700 hover:underline">Cookie Policy</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">9. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-700"><strong>QuickFinance.io</strong> — a trading style of Connexis Group Ltd</p>
                <p className="text-slate-600">Email: privacy@quickfinance.io</p>
                <p className="text-slate-600">Email: hello@quickfinance.io</p>
                <p className="text-slate-600">Address: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</p>
                <p className="text-slate-600">Company No. 17214446 | ICO Registration No. ZC148599</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">10. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
